import fragSource from './sting.frag.glsl?raw';
import vertSource from './sting.vert.glsl?raw';

// The measured raw WebGL2 sting, ported from
// docs/glasses-client/effects-lab/harness-webgl-raw/app.js.
//
// Everything expensive happens once at setup: the program links, the mark
// uploads as one texture, and the per-row noise texture is generated from a
// fixed seed. A frame is then one uniform write and one six-vertex draw.

const CANVAS_SIZE = 600;
const NOISE_W = 8;
// 600 / 80 gives a 7.5 px row pitch, matching the measured harness.
const NOISE_H = 80;
/** uMode 1 is the wave tear, the look that was preferred in the effects lab. */
const MODE_WAVE = 1;

export type WebglSting = {
  /** Draw one frame at `elapsedMs` milliseconds since the sting started. */
  drawAt(elapsedMs: number): void;
  /** Paint the canvas black, for the final frame after the fade. */
  clear(): void;
};

export type WebglStingOptions = {
  /**
   * Dev-only test hook (`?stingBreakShader=1`): feed the fragment shader a
   * source that cannot compile, so `createWebglSting` throws before doing
   * anything else. This is how the shader-compile-failure fallback path is
   * exercised in e2e tests, without a device that genuinely lacks WebGL2.
   */
  breakShader?: boolean;
};

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('could not create shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(`shader compile failed: ${gl.getShaderInfoLog(shader)}`);
  }
  return shader;
}

/** Per-row random red, filtered linearly so rows blend into a zigzag wave. */
function buildNoisePixels(): Uint8Array {
  const pixels = new Uint8Array(NOISE_W * NOISE_H * 4);
  let seed = 0x2f6e2b1;
  for (let y = 0; y < NOISE_H; y += 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const value = (seed >>> 24) & 255;
    for (let x = 0; x < NOISE_W; x += 1) {
      const offset = (y * NOISE_W + x) * 4;
      pixels[offset] = value;
      pixels[offset + 1] = 128;
      pixels[offset + 2] = 0;
      pixels[offset + 3] = 255;
    }
  }
  return pixels;
}

/**
 * Compile the sting shader against an existing WebGL2 context and upload the
 * mark. Throws if the program does not compile or link.
 */
export function createWebglSting(
  gl: WebGL2RenderingContext,
  markImage: TexImageSource,
  options: WebglStingOptions = {},
): WebglSting {
  // Garbage appended to a valid shader is still guaranteed not to compile,
  // which is all the break-shader test hook needs.
  const fragmentSource = options.breakShader ? `${fragSource}\nthis is not glsl;` : fragSource;

  const program = gl.createProgram();
  if (!program) throw new Error('could not create program');
  gl.attachShader(program, compile(gl, gl.VERTEX_SHADER, vertSource));
  gl.attachShader(program, compile(gl, gl.FRAGMENT_SHADER, fragmentSource));
  gl.bindAttribLocation(program, 0, 'aPos');
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(`link failed: ${gl.getProgramInfoLog(program)}`);
  }
  gl.useProgram(program);

  // One full-screen quad, two triangles.
  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

  // The mark on texture unit 0.
  const markTexture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, markTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, markImage);

  // The row noise on texture unit 1, repeating so the wave wraps.
  const noiseTexture = gl.createTexture();
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, noiseTexture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    NOISE_W,
    NOISE_H,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    buildNoisePixels(),
  );
  gl.activeTexture(gl.TEXTURE0);

  const uT = gl.getUniformLocation(program, 'uT');
  gl.uniform1i(gl.getUniformLocation(program, 'uTex'), 0);
  gl.uniform1i(gl.getUniformLocation(program, 'uNoise'), 1);
  gl.uniform1i(gl.getUniformLocation(program, 'uMode'), MODE_WAVE);
  gl.uniform2f(gl.getUniformLocation(program, 'uPx'), 1 / CANVAS_SIZE, 1 / CANVAS_SIZE);

  // The glasses display adds light to black, so the sting composites additively.
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE);
  gl.viewport(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);

  return {
    drawAt(elapsedMs: number): void {
      // The shader takes seconds since the sting started and derives every beat
      // envelope itself, so there is no accumulated delta to drift.
      gl.uniform1f(uT, elapsedMs / 1000);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    },
    clear(): void {
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    },
  };
}
